// Text-chat mode for <speechify-agent>. Unlike voice (LiveKit realtime),
// text mode drives the HTTP text channel one turn at a time. The widget
// owns the client contract — a stable client message id per user turn
// (sent as the Idempotency-Key), one message in flight at a time, the
// retriable 429/5xx backoff, the transparent unresumable restart (the
// server does it; we just render the reply), and the 402 "not in plan"
// degrade — but the transport that actually holds the credential and
// talks to the API is INJECTED, so the browser never sees an API key.
// The console preview wires its session-authenticated client as the
// first transport; a customer embed wires their own server relay (see
// README). Keyless public admission (a browser with no backend relay) is
// out of scope here.

/** A created text conversation. `conversationId` is the `conv_…` id. */
export interface TextConversationRef {
  conversationId: string;
}

/** One text turn's outcome, mapped from the API's TextMessageResult. */
export interface TextTurnResult {
  /** The agent's reply for this turn. Empty on a tool-only / silent turn. */
  reply: string;
  /** `awaiting_turn` (send more) or `completed` (the conversation ended). */
  status: string;
  /** True when the turn reached a terminal node. */
  ended: boolean;
}

/**
 * The pluggable transport the widget drives in text mode. Implementations
 * own auth (they hold the bearer) and map the two HTTP endpoints:
 *   - createConversation → POST /v1/agents/{id}/text-conversations
 *   - sendMessage        → POST …/text-conversations/{cid}/messages
 * On a non-2xx they MUST throw a {@link TextChatError} carrying the status
 * (and Retry-After, when present) so the widget can branch correctly.
 * `clientMessageId` is the widget-minted id the transport sends as the
 * `Idempotency-Key` header.
 */
export interface TextChatTransport {
  createConversation(): Promise<TextConversationRef>;
  sendMessage(req: { conversationId: string; message: string; clientMessageId: string }): Promise<TextTurnResult>;
}

/**
 * A transport failure carrying the HTTP disposition the widget branches on.
 * Transports throw this (or any error with a numeric `status`) from
 * create/send on a non-2xx. `notEntitled` (402 → degrade) and `retriable`
 * (429/5xx → back off and retry the SAME clientMessageId) are derived from
 * `status`; `retryAfterMs` comes from the Retry-After header.
 */
export class TextChatError extends Error {
  readonly status?: number;
  readonly retryAfterMs?: number;
  readonly notEntitled: boolean;
  readonly retriable: boolean;

  constructor(message: string, opts: { status?: number; retryAfterMs?: number } = {}) {
    super(message);
    this.name = "TextChatError";
    this.status = opts.status;
    this.retryAfterMs = opts.retryAfterMs;
    this.notEntitled = isNotEntitled(opts.status);
    this.retriable = isRetriable(opts.status);
  }
}

function isNotEntitled(status: number | undefined): boolean {
  return status === 402;
}

// A turn already in flight (429) or a transient runtime failure (5xx, e.g.
// 502) is retriable with the server's prior checkpoint intact. Everything
// else (400/401/403/404) is terminal for this turn.
function isRetriable(status: number | undefined): boolean {
  return status === 429 || (status !== undefined && status >= 500 && status < 600);
}

interface TurnErrorInfo {
  notEntitled: boolean;
  retriable: boolean;
  retryAfterMs?: number;
}

// Read the HTTP disposition off a thrown value STRUCTURALLY, never via
// `instanceof`. A transport injected from another bundle (the console
// preview loads the widget as a script-tag IIFE) throws its own error
// class, so identity checks would silently fail — branch on fields the
// transport contract guarantees instead: a numeric `status` (+ optional
// `retryAfterMs`), or pre-computed `notEntitled` / `retriable` booleans.
function readTurnError(err: unknown): TurnErrorInfo {
  if (err && typeof err === "object") {
    const e = err as { status?: unknown; retryAfterMs?: unknown; notEntitled?: unknown; retriable?: unknown };
    const status = typeof e.status === "number" ? e.status : undefined;
    return {
      notEntitled: e.notEntitled === true || isNotEntitled(status),
      retriable: e.retriable === true || isRetriable(status),
      retryAfterMs: typeof e.retryAfterMs === "number" ? e.retryAfterMs : undefined,
    };
  }
  return { notEntitled: false, retriable: false };
}

/** Callbacks the element wires to its DOM + public events. */
export interface TextChatCallbacks {
  onUserMessage(text: string, timestamp: number): void;
  onAssistantMessage(text: string, timestamp: number): void;
  /** Turn in flight (true) → false. Drives the thinking indicator + input lock. */
  onThinking(thinking: boolean): void;
  /** The conversation reached a terminal node; no more turns are possible. */
  onEnded(): void;
  /** A recoverable turn error — the input re-enables so the user can retry. */
  onError(err: Error): void;
  /** The text channel isn't available (402, or no transport). Terminal. */
  onUnavailable(reason: string): void;
}

// A turn that keeps getting refused (429) or failing transiently (5xx)
// shouldn't retry forever. Six attempts at ~1s Retry-After each is a
// generous ceiling for a single user turn before we surface an error.
const MAX_TURN_ATTEMPTS = 6;
const DEFAULT_RETRY_MS = 1000;

function newClientMessageId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `cmid-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

const realSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Drives a single text conversation over an injected transport. Owns the
 * whole client contract so the element only renders. Not DOM-aware and
 * fully synchronous in its branching, so it unit-tests against a fake
 * transport. `sleep` is injectable so tests don't wait on real timers.
 */
export class TextChatController {
  #transport: TextChatTransport;
  #cb: TextChatCallbacks;
  #sleep: (ms: number) => Promise<void>;

  #conversationId: string | null = null;
  #creating: Promise<void> | null = null;
  #sending = false;
  #ended = false;
  #unavailable = false;

  constructor(transport: TextChatTransport, cb: TextChatCallbacks, sleep: (ms: number) => Promise<void> = realSleep) {
    this.#transport = transport;
    this.#cb = cb;
    this.#sleep = sleep;
  }

  get ended(): boolean {
    return this.#ended;
  }

  get unavailable(): boolean {
    return this.#unavailable;
  }

  /**
   * Create the conversation up front so a 402 (text not in plan) surfaces
   * as "unavailable" the moment the panel opens, before the user types.
   * Idempotent: concurrent/repeat calls share the one in-flight create.
   */
  async ensureConversation(): Promise<boolean> {
    if (this.#unavailable) return false;
    if (this.#conversationId) return true;
    if (!this.#creating) {
      this.#creating = (async () => {
        try {
          const ref = await this.#transport.createConversation();
          this.#conversationId = ref.conversationId;
        } finally {
          this.#creating = null;
        }
      })();
    }
    try {
      await this.#creating;
      return this.#conversationId !== null;
    } catch (err) {
      this.#handleTerminalError(err);
      return false;
    }
  }

  /** Send one user turn. No-op while a turn is in flight, or once ended. */
  async send(text: string): Promise<void> {
    const message = text.trim();
    if (!message || this.#sending || this.#ended || this.#unavailable) return;

    this.#sending = true;
    this.#cb.onThinking(true);
    this.#cb.onUserMessage(message, Date.now());
    try {
      if (!(await this.ensureConversation()) || this.#conversationId === null) return;
      const clientMessageId = newClientMessageId();
      const res = await this.#sendWithRetry({ conversationId: this.#conversationId, message, clientMessageId });
      if (res.reply) this.#cb.onAssistantMessage(res.reply, Date.now());
      if (res.ended || res.status === "completed") {
        this.#ended = true;
        this.#cb.onEnded();
      }
    } catch (err) {
      this.#handleTerminalError(err);
    } finally {
      this.#sending = false;
      this.#cb.onThinking(false);
    }
  }

  async #sendWithRetry(req: {
    conversationId: string;
    message: string;
    clientMessageId: string;
  }): Promise<TextTurnResult> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.#transport.sendMessage(req);
      } catch (err) {
        const info = readTurnError(err);
        if (!info.retriable || attempt >= MAX_TURN_ATTEMPTS - 1) throw err;
        // Retry the SAME clientMessageId: consecutive-redelivery dedup +
        // the idempotency middleware make the retry exactly-once.
        await this.#sleep(info.retryAfterMs ?? DEFAULT_RETRY_MS);
      }
    }
  }

  #handleTerminalError(err: unknown): void {
    if (readTurnError(err).notEntitled) {
      this.#unavailable = true;
      this.#cb.onUnavailable("Text chat isn't available for this agent.");
      return;
    }
    this.#cb.onError(err instanceof Error ? err : new Error(String(err)));
  }
}
